import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

import { cn, sectionNumber } from '@/lib/utils';
import { AgentAvatar, agentFullName } from './AgentAvatar';
import type { AgentId } from '@/hooks/useJobProgress';

interface ReviewPanelProps {
  agent: AgentId;
  /** Pipeline-position label (e.g. "01" for ORA). */
  index: number;
  iteration?: number;
  reviewStatus?: string;
  /** Title rendered to the right of the agent name (e.g. "Intent descriptor"). */
  subtitle?: ReactNode;
  /** Tag rendered far-right above the body (e.g. "1 of 3 pages"). */
  rightTag?: ReactNode;
  children: ReactNode;
}

const STATUS_TONE: Record<string, string> = {
  pending: 'text-graphite',
  awaiting_user: 'text-ochre',
  approved: 'text-moss',
  revising: 'text-ochre',
  superseded: 'text-graphite/60',
  skipped: 'text-graphite/60',
};

/**
 * Common chrome for every review panel: agent monogram + name + iteration
 * badge + review-status tag, then a hairline rule, then the slot.
 */
export function ReviewPanel({
  agent,
  index,
  iteration,
  reviewStatus,
  subtitle,
  rightTag,
  children,
}: ReviewPanelProps) {
  return (
    <section className="space-y-6">
      <header className="flex items-start justify-between gap-6">
        <div className="flex items-center gap-3">
          <span className="font-mono text-[11px] text-graphite tabular-nums">
            {sectionNumber(index)}
          </span>
          <AgentAvatar agent={agent} size="md" />
          <div className="flex flex-col">
            <h2 className="font-serif text-display-sm italic leading-tight text-ink">
              {agentFullName(agent)}
              {subtitle ? <span className="text-graphite"> — {subtitle}</span> : null}
            </h2>
            {(iteration || reviewStatus) && (
              <p className="mt-0.5 flex items-center gap-3 font-mono text-[11px]">
                {iteration ? (
                  <span className="text-graphite">iteration {iteration}</span>
                ) : null}
                {reviewStatus ? (
                  <span className={cn('smallcaps', STATUS_TONE[reviewStatus] ?? 'text-graphite')}>
                    {reviewStatus.replace(/_/g, ' ')}
                  </span>
                ) : null}
              </p>
            )}
          </div>
        </div>
        {rightTag ? (
          <span className="smallcaps text-graphite">{rightTag}</span>
        ) : null}
      </header>

      <div className="border-t border-ink/10 pt-6">{children}</div>
    </section>
  );
}

/**
 * Empty-state body for a panel that has no outputs yet. When the agent
 * is currently running (per the job state) the copy switches from
 * "<AGENT> hasn't run yet." to a live "<AGENT> running…" indicator so
 * users get feedback while the model is producing its first artefact.
 */
export function AgentEmptyState({
  agent,
  isRunning,
  idleMessage,
}: {
  agent: AgentId;
  isRunning?: boolean;
  /** Override copy for the not-running state (default: "<AGENT> hasn't run yet."). */
  idleMessage?: string;
}) {
  const name = agentFullName(agent);
  if (isRunning) {
    return (
      <p className="flex items-center gap-2 text-graphite">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-ochre" aria-hidden />
        <span>{name} running…</span>
      </p>
    );
  }
  return <p className="text-graphite">{idleMessage ?? `${name} hasn't run yet.`}</p>;
}
